import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { Password } from 'primereact/password';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState('login'); // 'login' or '2fa'
  const [loginEmail, setLoginEmail] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API_BASE_URL}/auth/login`, {
        email,
        password
      });

      if (response.data.requires2FA) {
        setLoginEmail(response.data.email || email);
        setStep('2fa');
      } else {
        localStorage.setItem('adminToken', response.data.token);
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handle2FA = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API_BASE_URL}/auth/verify-2fa`, {
        email: loginEmail || email,
        token: twoFactorCode
      });

      localStorage.setItem('adminToken', response.data.token);
      
      // Check if user has 2FA enabled
      try {
        const userResponse = await axios.get(`${API_BASE_URL}/auth/me`, {
          headers: {
            Authorization: `Bearer ${response.data.token}`
          }
        });
        
        if (!userResponse.data.twoFactorEnabled) {
          // User must enable 2FA before accessing the dashboard
          navigate('/require-2fa');
        } else {
          navigate('/dashboard');
        }
      } catch (err) {
        // If we can't check 2FA status, redirect to require-2fa to be safe
        navigate('/require-2fa');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid 2FA code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const cardStyle = {
    maxWidth: '440px',
    width: '100%',
    margin: '0 auto',
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
    borderRadius: '12px'
  };

  const header = (
    <div style={{ 
      textAlign: 'center', 
      padding: '2rem 2rem 1.5rem 2rem',
      borderBottom: '1px solid #e9ecef'
    }}>
      <div style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '64px',
        height: '64px',
        borderRadius: '50%',
        backgroundColor: '#e7f3ff',
        marginBottom: '1.5rem'
      }}>
        <i className="pi pi-lock" style={{ 
          fontSize: '2rem', 
          color: '#0066cc'
        }}></i>
      </div>
      <h2 style={{ 
        margin: 0, 
        fontSize: '1.75rem',
        fontWeight: '600',
        color: '#2c3e50',
        marginBottom: '0.5rem'
      }}>
        Admin Dashboard
      </h2>
      <p style={{ 
        margin: 0, 
        color: '#6c757d',
        fontSize: '0.95rem'
      }}>
        {step === 'login' ? 'Sign in to continue' : 'Enter your 2FA code'}
      </p>
    </div>
  );

  return (
    <div style={{ 
      minHeight: '100vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      backgroundColor: '#f5f6fa', 
      padding: '1.5rem'
    }}>
      <Card style={cardStyle} header={header}>
        <div style={{ padding: '2rem' }}>
          {error && (
            <Message 
              severity="error" 
              text={error} 
              style={{ marginBottom: '1.5rem' }} 
              onClose={() => setError('')} 
            />
          )}

          {step === 'login' ? (
            <form onSubmit={handleLogin}>
              <div className="p-field" style={{ marginBottom: '1.75rem' }}>
                <label 
                  htmlFor="email" 
                  className="p-d-block" 
                  style={{ 
                    marginBottom: '0.75rem', 
                    fontWeight: '600',
                    color: '#495057',
                    fontSize: '0.95rem'
                  }}
                >
                  Email
                </label>
                <InputText
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={{ 
                    width: '100%',
                    padding: '0.75rem',
                    fontSize: '1rem'
                  }}
                  required
                  autoFocus
                  disabled={loading}
                  placeholder="Enter your email"
                />
              </div>

              <div className="p-field" style={{ marginBottom: '2rem' }}>
                <label 
                  htmlFor="password" 
                  className="p-d-block" 
                  style={{ 
                    marginBottom: '0.75rem', 
                    fontWeight: '600',
                    color: '#495057',
                    fontSize: '0.95rem'
                  }}
                >
                  Password
                </label>
                <Password
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={{ width: '100%' }}
                  inputStyle={{ 
                    width: '100%',
                    padding: '0.75rem',
                    fontSize: '1rem',
                    height: 'auto',
                    lineHeight: '1.5'
                  }}
                  panelStyle={{ display: 'none' }}
                  feedback={false}
                  required
                  disabled={loading}
                  toggleMask
                  placeholder="Enter your password"
                />
              </div>

              <Button
                type="submit"
                label="Sign In"
                icon={loading ? null : "pi pi-sign-in"}
                iconPos="left"
                style={{ 
                  width: '100%',
                  padding: '0.75rem',
                  fontSize: '1rem',
                  fontWeight: '500',
                  minHeight: '3rem'
                }}
                className="p-button-lg"
                disabled={loading}
                loading={loading}
              />
            </form>
          ) : (
            <form onSubmit={handle2FA}>
              <div className="p-field" style={{ marginBottom: '2rem' }}>
                <label 
                  htmlFor="2fa" 
                  className="p-d-block" 
                  style={{ 
                    marginBottom: '0.75rem', 
                    fontWeight: '600',
                    color: '#495057',
                    fontSize: '0.95rem'
                  }}
                >
                  2FA Verification Code
                </label>
                <InputText
                  id="2fa"
                  type="text"
                  value={twoFactorCode}
                  onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  style={{ 
                    width: '100%', 
                    textAlign: 'center', 
                    fontSize: '1.75rem', 
                    letterSpacing: '0.75rem',
                    padding: '1rem',
                    fontWeight: '600',
                    fontFamily: 'monospace'
                  }}
                  maxLength={6}
                  placeholder="000000"
                  required
                  autoFocus
                  disabled={loading}
                />
                <small className="p-d-block" style={{ 
                  marginTop: '0.75rem', 
                  color: '#6c757d',
                  fontSize: '0.875rem',
                  textAlign: 'center',
                  lineHeight: '1.5'
                }}>
                  Enter the 6-digit code from your authenticator app
                </small>
              </div>

              <Button
                type="submit"
                label="Verify"
                icon={loading ? null : "pi pi-check"}
                iconPos="left"
                style={{ 
                  width: '100%', 
                  marginBottom: '0.75rem',
                  padding: '0.75rem',
                  fontSize: '1rem',
                  fontWeight: '500',
                  minHeight: '3rem'
                }}
                className="p-button-lg"
                disabled={loading || twoFactorCode.length !== 6}
                loading={loading}
              />

              <Button
                type="button"
                label="Back to Login"
                icon="pi pi-arrow-left"
                iconPos="left"
                style={{ 
                  width: '100%',
                  padding: '0.75rem',
                  fontSize: '1rem'
                }}
                className="p-button-text p-button-lg"
                onClick={() => {
                  setStep('login');
                  setTwoFactorCode('');
                  setError('');
                }}
                disabled={loading}
              />
            </form>
          )}
        </div>
      </Card>
    </div>
  );
};

export default Login;
